import { useState, type ReactNode } from 'react';
import { useT } from '@hana/i18n';
import './screens.i18n';
import {
  IconChevronDown, IconChevronLeft, IconCircleCheck, IconClipboardList, IconDots, IconExternalLink, IconFileText, IconHistory, IconId, IconInfoCircle,
  IconLink, IconMessageCircle, IconPlus, IconUserOff, IconUsers, IconChecklist, IconChevronRight,
} from '@tabler/icons-react';
import { useList } from '@hana/data';
import { PageHeader } from '@hana/shell';
import { Button, Segmented, useToast } from '@hana/ui';
import type { SeatKind, Staff, StaffStatus } from './types';
import s from './detail.module.css';

// Mint-HR-iPad-Detail: trang hồ sơ nhân sự trên iPad (script board `d21Kit`, tab Hồ sơ). Chỉ đọc, không lương / CCCD.
// Tab Chấm công / Nghỉ phép / Lương chưa mở ở bản này (toast).

type Tab = 'staff' | 'time' | 'leave' | 'pay';
const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'staff', label: 'Hồ sơ' },
  { key: 'time', label: 'Chấm công' },
  { key: 'leave', label: 'Nghỉ phép' },
  { key: 'pay', label: 'Lương' },
];
const KIND_TONE: Record<SeatKind, string> = { Primary: s.tMute ?? '', Concurrent: s.tWarn ?? '', Acting: s.tPri ?? '' };
const ST_TONE: Record<StaffStatus, string> = { 'Đang làm': s.tOk ?? '', 'Nghỉ phép': s.tWarn ?? '', 'Thử việc': s.tPri ?? '' };
const tail = (u: string) => u.replace(/^\[[^\]]*\]\s*/, '');

/** Chuỗi duyệt theo ghế chính (board `boss`): người cuối chuỗi là cấp trên. */
function bossOf(x: Staff): string {
  const c = x.seats[0]?.code ?? '';
  return x.id !== 'PHILIP' && /TRD|OPS/.test(c) ? 'PHONGNT' : 'PHILIP';
}

const ini = (u: string) => u.slice(0, 2).toUpperCase();

interface Todo { id: string; k: string; who: string; due: string }
interface Act { k: string; who: string; when: string; kind: string }

function Card({ id, title, tip, icon, count, badge, open, onToggle, children }: {
  id: string; title: string; tip: string; icon: ReactNode; count?: string; badge?: number; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <section className={s.card} aria-label={title} data-card={id}>
      <div className={s.hd}>
        <span className={s.hdIc} aria-hidden="true">{icon}</span>
        <h2>{title}</h2>
        <span className={s.tip} role="img" aria-label={`Giải thích: ${tip}`} title={tip}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>
        {count && <span className={s.cnt}>{count}</span>}
        {!!badge && <span className={s.badge}>{badge}</span>}
        <button type="button" className={s.tog} aria-expanded={open} aria-label={`Mở hoặc gập ${title}`} onClick={onToggle}>
          <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
        </button>
      </div>
      {open && <div className={s.body}>{children}</div>}
    </section>
  );
}

export function HrDetailPad({ staff, onBack }: { staff: Staff; onBack: () => void }) {
  const t = useT();
  const toast = useToast();
  const { data: org } = useList('hrOrgBlock', { limit: 50 });
  const [tab, setTab] = useState<Tab>('staff');
  const [menu, setMenu] = useState(false);
  const [openC, setOpenC] = useState<Record<string, boolean>>({ sum: true, seats: true, docs: true, act: false, todo: true, ppl: true, links: false });
  const [done, setDone] = useState<Record<string, boolean>>({});
  const [allAct, setAllAct] = useState(false);
  const tog = (k: string) => () => setOpenC((o) => ({ ...o, [k]: !o[k] }));
  const noWrite = () => toast({ msg: t('hr.s.noWrite') });

  const seatName = (code: string) => org?.items.flatMap((b) => b.depts.flatMap((d) => d.seats)).find((x) => x.code === code)?.name ?? code;
  const probation = staff.status === 'Thử việc';
  const docs: Array<[string, string, boolean]> = [
    ['Hợp đồng lao động', probation ? 'HĐ thử việc · hết 02/Jan/2027' : 'Không xác định thời hạn', probation],
    ['BHXH', 'Đã đăng ký', false],
    ['Đồng ý lưu dữ liệu cá nhân', 'Có · 12/Jan/2026', false],
    ['Chứng chỉ', staff.id === 'PHONGNT' ? 'ATVSLĐ hết hạn sau 30 ngày' : '—', staff.id === 'PHONGNT'],
  ];
  const todos: Todo[] = [
    ...(probation ? [{ id: 'tv', k: 'HĐ thử việc hết 02/Jan/2027 · đánh giá', who: 'HANHLTM', due: 'trước 02/Jan/2027' }] : []),
    ...(staff.id === 'PHONGNT' ? [{ id: 'cc', k: 'Chứng chỉ ATVSLĐ hết hạn sau 30 ngày', who: 'HANHLTM', due: 'còn 30 ngày' }] : []),
  ];
  const openN = todos.filter((t) => !done[t.id]).length;
  const acts: Act[] = [
    { k: `Gửi đơn phép năm · 03–05/Nov/2026`, who: staff.id, when: '21/Oct/2026', kind: 'Đơn' },
    { k: `Cập nhật ghế ${staff.seats.map((h) => h.code).join(' + ')}`, who: 'HANHLTM', when: '05/Oct/2026 09:00', kind: 'Ghế' },
    { k: 'Đồng bộ ảnh đại diện M365', who: 'Hệ thống', when: '01/Oct/2026 02:00', kind: 'Hệ thống' },
  ];
  const boss = bossOf(staff);
  const ppl = [
    { u: staff.id, role: `Chính chủ · ${staff.seats[0]?.code ?? ''}`, hei: staff.id !== 'DAVID' },
    { u: boss, role: 'Cấp trên theo ghế', hei: true },
    { u: staff.backup, role: 'Người thay (backup)', hei: true },
    { u: 'HANHLTM', role: '[HRD-MGR · kiêm] HR phụ trách', hei: true },
  ].filter((x, i, a) => x.u !== '—' && a.findIndex((y) => y.u === x.u) === i);
  const links: ReadonlyArray<readonly [string, string, () => void]> = [
    ['Hồ sơ đầy đủ', 'Mở trên máy tính · Hồ sơ nhân sự', () => toast({ msg: 'Hồ sơ đầy đủ chưa mở ở bản này' })],
    ['Quyết định nhân sự', 'Tăng lương, điều chuyển, kiêm nhiệm', () => toast({ msg: 'Quyết định nhân sự chưa mở ở bản này' })],
    ['Danh sách nhân sự', 'Về danh sách', onBack],
  ];

  return (
    <>
      <PageHeader
        title={`${staff.id} · ${staff.name}`}
        lead={<button type="button" className={s.back} aria-label="Về danh sách nhân sự" title="Về danh sách nhân sự" onClick={onBack}><IconChevronLeft size={20} stroke={2} aria-hidden="true" /></button>}
        tabs={<Segmented label="Mục" value={tab} onChange={(k: Tab) => (k === 'staff' ? setTab(k) : toast({ msg: 'Tab này chưa mở ở bản này' }))} items={TABS} />}
        actions={
          <>
            <div className={s.menuWrap}>
              <button type="button" className={s.menuBtn} aria-label="Thêm thao tác" title="Thêm thao tác" aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
                <IconDots size={20} stroke={2.6} aria-hidden="true" />
              </button>
              {menu && (
                <div role="menu" aria-label="Thêm thao tác" className={s.menu}>
                  <button type="button" role="menuitem" className={s.mi} onClick={() => { setMenu(false); toast({ msg: 'Uỷ quyền ghế khi vắng chưa mở ở bản này' }); }}>
                    <IconUserOff size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.absentTodayShort')}
                  </button>
                  <button type="button" role="menuitem" className={s.mi} onClick={() => { setMenu(false); toast({ msg: 'Hồ sơ đầy đủ chưa mở ở bản này' }); }}>
                    <IconExternalLink size={16} stroke={1.8} aria-hidden="true" />{t('hr.s.openFull')}
                  </button>
                </div>
              )}
            </div>
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{t('hr.s.leaveReq')}</Button>
          </>
        }
      />
      <div className={s.grid}>
        <div className={s.col}>
          <Card id="sum" title="Tóm tắt" tip="Người đang xem, phòng, ngày vào và ghế đang giữ (Primary / kiêm / quyền theo TblOrgAssign). Đổi người: ← về danh sách." icon={<IconId size={18} stroke={1.9} />} open={!!openC.sum} onToggle={tog('sum')}>
            <div className={s.me}>
              <span className={s.avL} aria-hidden="true">{ini(staff.id)}</span>
              <div className={s.who}>
                <b>{staff.name}</b>
                <div>{staff.id} · {staff.dept} · vào {staff.joined}</div>
                <div className={s.chips}>
                  {staff.seats.map((h) => <span key={h.code} className={`${s.chip} ${KIND_TONE[h.kind]}`} title={`${seatName(h.code)} · ${h.kind}`}>{h.code}</span>)}
                </div>
              </div>
              <span className={`${s.st} ${ST_TONE[staff.status]}`}>{staff.status}</span>
            </div>
          </Card>
          <Card id="docs" title="Hồ sơ" tip="Hợp đồng, BHXH, đồng ý lưu dữ liệu cá nhân và chứng chỉ. Hồ sơ đầy đủ mở trên máy tính." icon={<IconFileText size={18} stroke={1.9} />} open={!!openC.docs} onToggle={tog('docs')}>
            {docs.map(([k, v, warn]) => <div key={k} className={s.kv}><span>{k}</span><span className={warn ? s.warnV : undefined}>{v}</span></div>)}
          </Card>
          <Card id="act" title="Hoạt động" tip="Nhật ký hồ sơ: sửa mục, đơn nghỉ, báo vắng, đồng bộ ảnh đại diện." icon={<IconHistory size={18} stroke={1.9} />} count={`${acts.length} mục`} open={!!openC.act} onToggle={tog('act')}>
            {(allAct ? acts : acts.slice(0, 5)).map((a) => (
              <div key={a.k} className={s.actRow}>
                <span className={s.avS} aria-hidden="true">{ini(a.who)}</span>
                <span className={s.actTx}><span title={a.k}>{a.k}</span><span>{a.who} · {a.when}</span></span>
                <span className={s.kind}>{a.kind}</span>
              </div>
            ))}
            {acts.length > 5 && <button type="button" className={s.more} onClick={() => setAllAct((v) => !v)}>{allAct ? 'Thu gọn' : `Xem thêm ${acts.length - 5}`}</button>}
          </Card>
        </div>
        <div className={s.col}>
          <Card id="seats" title="Ghế đang giữ" tip="Ghế theo TblOrgAssign: chính · kiêm · quyền." icon={<IconUsers size={18} stroke={1.9} />} open={!!openC.seats} onToggle={tog('seats')}>
            {staff.seats.map((h) => (
              <div key={h.code} className={s.seatRow}>
                <span className={`${s.chip} ${KIND_TONE[h.kind]}`}>{h.code}</span>
                <span>{seatName(h.code)}</span>
                <span>{h.kind}</span>
              </div>
            ))}
          </Card>
          <Card id="todo" title="Việc cần làm" tip="Việc hồ sơ: duyệt đơn đang chờ, HĐ thử việc sắp hết hạn, chứng chỉ hết hạn, công thiếu." icon={<IconChecklist size={18} stroke={1.9} />} badge={openN} open={!!openC.todo} onToggle={tog('todo')}>
            {todos.map((t) => (
              <div key={t.id} className={s.todo}>
                <button type="button" className={s.chk} role="checkbox" aria-checked={!!done[t.id]} aria-label={`Xong: ${t.k}`} onClick={() => setDone((d) => ({ ...d, [t.id]: !d[t.id] }))}>{done[t.id] ? '✓' : ''}</button>
                <span className={s.todoTx}><span title={t.k}>{t.k}</span><span>{t.who} · {t.due}</span></span>
              </div>
            ))}
            {!todos.length && <div className={s.none}>{t('hr.s.noTodo')}</div>}
          </Card>
          <Card id="ppl" title="Người phụ trách" tip="Avatar lấy từ TblStaff.ImageProfile (HanaAvatar), chưa có ảnh thì hiện chữ cái. Người duyệt hiện theo ghế (TblOrgAssign)." icon={<IconClipboardList size={18} stroke={1.9} />} open={!!openC.ppl} onToggle={tog('ppl')}>
            {ppl.map((u) => (
              <div key={u.u} className={s.ppl}>
                <span className={s.avM} aria-hidden="true">{ini(tail(u.u))}</span>
                <span className={s.pplTx}><b>{u.u}</b><span>{u.role}</span></span>
                {u.hei && <a className={s.hei} href="/hei/" target="_blank" rel="noopener" aria-label={`Nhắn ${u.u} trong HANA Hei`} title="Nhắn trong HANA Hei"><IconMessageCircle size={18} stroke={1.9} aria-hidden="true" /></a>}
              </div>
            ))}
          </Card>
          <Card id="links" title="Liên kết" tip="Lối sang màn liên quan. Mở trong cùng HANA Ezy, giữ ngữ cảnh bản ghi." icon={<IconLink size={18} stroke={1.9} />} open={!!openC.links} onToggle={tog('links')}>
            {links.map(([k, sub, go]) => (
              <button key={k} type="button" className={s.link} onClick={go}>
                <IconCircleCheck size={18} stroke={1.9} aria-hidden="true" />
                <span><span>{k}</span><span>{sub}</span></span>
                <IconChevronRight size={16} aria-hidden="true" />
              </button>
            ))}
          </Card>
        </div>
      </div>
    </>
  );
}
